// ============================================================================
// Wer bekommt die Verkaufsbedingungen aus dem Mapping geschrieben?
//
// Bewusst DOM- und netzfrei, damit die Auswahl testbar ist -- siehe
// variant-offer-overrides.test.ts. Dasselbe Muster wie lib/variant-attribute-selection.ts.
// ============================================================================

/** Was diese Auswahl von einer Varianten-Zeile braucht. Absichtlich ein Minimal-Vertrag:
 *  So kann der Test mit Attrappen arbeiten, statt eine ganze VariantCheckRow zu bauen. */
export interface ZeileFuerVerkaufsbedingungen {
  articleId: string;
  listingId: string | null;
  isParent: boolean;
  /** Der aus dem Mapping aufgeloeste Angebots-Payload aus der Vorschau-Function.
   *  Fehlt, solange die alte Function ausgeliefert ist. */
  unitPayload?: Record<string, unknown> | undefined;
  /** Ob im Snapshot bereits brauchbare Verkaufsbedingungen stehen. Kommt vom
   *  `variant-check`. FEHLT er, wird NICHT geschrieben — siehe
   *  `zeilenFuerVerkaufsbedingungen`. */
  hatVerkaufsbedingungen?: boolean | undefined;
}

/** Ein Preis, mit dem der Push etwas anfangen kann: Zahl, echt groesser null.
 *
 *  DIESELBE SCHWELLE an drei Stellen, absichtlich und nicht zusammenfuehrbar, weil jede
 *  eine andere Datenform prueft:
 *    1. `apps/worker/src/jobs/kaufland-publish-listing.ts` -- der gespeicherte Override.
 *       Das ist die massgebliche Stelle, sie wirft `listing_price (cents, > 0) missing`.
 *    2. `supabase/functions/kaufland-listing-preview/index.ts` (`hatVerkaufsbedingungen`)
 *       -- der Snapshot in der Datenbank, fuer das Flag am `variant-check`.
 *    3. hier -- der frisch aufgeloeste `unitPayload` aus der Vorschau.
 *  Wer die Schwelle aendert, aendert sie an allen dreien. */
function istBrauchbarerPreis(v: unknown): boolean {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

/**
 * Die Zeilen, denen die Verkaufsbedingungen aus dem Mapping geschrieben werden.
 *
 * Anlass (2026-08-17): "Alle fehlenden anlegen" legte die Kind-Listings an, schrieb aber
 * keine `offerOverrides` in den Snapshot. Der Push scheiterte an
 * `listing_price (cents, > 0) missing in offerOverrides`.
 *
 * ERSTE FASSUNG war auf die GERADE angelegten Kinder eingegrenzt, damit ein zweiter Klick
 * die von Hand gepflegten Preise der Geschwister nicht ueberschreibt. Der Schutz war
 * richtig, das Kriterium falsch: Fuenf aeltere Zeilen blieben dauerhaft ohne Bedingungen,
 * weil sie vor der Funktion entstanden -- und der Knopf "Alle fehlenden anlegen"
 * verschwindet, sobald alle Kinder existieren. Es gab damit keinen Auslauf mehr, der sie
 * je erreicht haette.
 *
 * JETZT entscheidet der IST-Zustand statt des Anlagezeitpunkts: geschrieben wird nur, wo
 * noch keine brauchbaren Bedingungen stehen. Das schuetzt gepflegte Preise genauso -- sie
 * sind ja vorhanden -- und deckt die Altfaelle mit ab. Frisch angelegte Kinder haben nie
 * welche und fallen automatisch hinein; der `neuAngelegt`-Parameter ist deshalb entfallen.
 *
 * Der Vater faellt raus: Er wird bei Kaufland nie gelistet, ein Angebotspreis an ihm hat
 * keine Bedeutung.
 *
 * Ohne aufloesbaren Preis im `unitPayload` wird NICHT geschrieben. Ein Preis 0 bei Kaufland
 * waere schlimmer als eine Luecke (CLAUDE.md, Abschnitt Preis-Push).
 *
 * FEHLENDES Flag heisst "anfassen verboten", nicht "ungepflegt": Verlangt wird ausdruecklich
 * `=== false`. Das Flag fehlt, wenn die Datenbankabfrage im `variant-check` gescheitert ist
 * oder noch die alte Function ausgeliefert wird. Wuerde `undefined` als "ungepflegt" gelten,
 * boete der Tab in genau diesen Faellen an, gepflegte Preise zu ueberschreiben — der
 * teuerste denkbare Fehlschluss aus einem Datenbankfehler.
 *
 * Pure -- keine IO.
 */
export function zeilenFuerVerkaufsbedingungen<T extends ZeileFuerVerkaufsbedingungen>(
  rows: readonly T[],
): T[] {
  return rows.filter((r) =>
    !r.isParent
    && r.hatVerkaufsbedingungen === false
    && typeof r.listingId === 'string'
    && r.listingId.length > 0
    && r.unitPayload !== undefined
    && r.unitPayload !== null
    && istBrauchbarerPreis(r.unitPayload['listing_price'])
  );
}
